import React from "react";
import PropTypes from "prop-types";
import styled, { keyframes, css } from "styled-components";
import { Link as ReactRouterLink } from "react-router-dom";
import { useTranslation } from "react-i18next";

const livePulse = keyframes`
  0%   { box-shadow: 0 0 0 0 rgba(211, 47, 47, 0.35); }
  50%  { box-shadow: 0 0 0 8px rgba(211, 47, 47, 0); }
  100% { box-shadow: 0 0 0 0 rgba(211, 47, 47, 0); }
`;

const SLOT_BORDER = {
  live: "rgba(211, 47, 47, 0.35)",
  absent: "rgba(183, 28, 28, 0.25)",
  present: "rgba(27, 94, 32, 0.28)",
  neutral: "rgba(0, 48, 59, 0.12)"
};

const SLOT_BACKGROUND = {
  live: "linear-gradient(105deg, rgba(211, 47, 47, 0.1) 0%, #fff 55%)",
  absent: "rgba(183, 28, 28, 0.06)",
  present: "rgba(46, 125, 50, 0.07)",
  neutral: "#fafbfc"
};

const TONE_COLOR = {
  live: "#b71c1c",
  absent: "#b71c1c",
  present: "#1b5e20"
};

const ATTENDANCE_COLOR = {
  absent: "#b71c1c",
  present: "#1b5e20",
  unknown: "#546e7a"
};

const ATTENDANCE_BACKGROUND = {
  absent: "rgba(183, 28, 28, 0.1)",
  present: "rgba(46, 125, 50, 0.12)",
  unknown: "rgba(84, 110, 122, 0.12)"
};

const Band = styled.section`
  display: flex;
  flex-direction: column;
  gap: 10px;
  width: 100%;
  margin: 4px 0 8px;
`;

const Timeline = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  gap: 10px;
  width: 100%;
`;

const Slot = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 12px 14px;
  border-radius: 8px;
  border: 1px solid ${({ $tone }) => SLOT_BORDER[$tone] || SLOT_BORDER.neutral};
  background: ${({ $tone }) =>
    SLOT_BACKGROUND[$tone] || SLOT_BACKGROUND.neutral};
  ${({ $tone }) =>
    $tone === "live" &&
    css`
      animation: ${livePulse} 2s ease-in-out infinite;
    `};
`;

const SlotLabel = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: ${({ $tone, theme }) => TONE_COLOR[$tone] || theme.color.default.main};
`;

const Dot = styled.span`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #d32f2f;
  flex-shrink: 0;
`;

const SlotTitle = styled.h3`
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: ${({ theme }) => theme.color.secondary.dark};
  line-height: 1.25;
`;

const SlotMeta = styled.p`
  margin: 0;
  font-size: 13px;
  color: ${({ theme }) => theme.color.default.dark};
`;

const AttendanceBadge = styled.span`
  display: inline-flex;
  align-self: flex-start;
  margin-top: 2px;
  padding: 2px 8px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 600;
  color: ${({ $state }) =>
    ATTENDANCE_COLOR[$state] || ATTENDANCE_COLOR.unknown};
  background: ${({ $state }) =>
    ATTENDANCE_BACKGROUND[$state] || ATTENDANCE_BACKGROUND.unknown};
`;

const ScheduleLink = styled(ReactRouterLink)`
  align-self: flex-start;
  margin-top: 4px;
  font-size: 13px;
  font-weight: 500;
  color: ${({ theme }) => theme.color.primary.dark};
  text-decoration: none;
  &:hover {
    text-decoration: underline;
  }
`;

const EmptyHint = styled.p`
  margin: 0;
  padding: 6px 4px;
  font-size: 14px;
  color: ${({ theme }) => theme.color.default.main};
`;

const CompactWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  width: 100%;
  min-width: 0;
`;

const CompactChip = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 6px 8px;
  border-radius: 6px;
  border: 1px solid ${({ $tone }) => SLOT_BORDER[$tone] || SLOT_BORDER.neutral};
  background: ${({ $tone }) =>
    SLOT_BACKGROUND[$tone] || SLOT_BACKGROUND.neutral};
  min-width: 0;
`;

const CompactChipHead = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 6px;
  min-width: 0;
`;

const CompactChipLabel = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: ${({ $tone, theme }) => TONE_COLOR[$tone] || theme.color.default.main};
  flex-shrink: 0;
`;

const CompactChipTitle = styled.span`
  display: block;
  font-size: 13px;
  font-weight: 600;
  color: ${({ theme }) => theme.color.secondary.dark};
  line-height: 1.25;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const CompactChipMeta = styled.span`
  display: block;
  font-size: 11px;
  font-weight: 500;
  color: ${({ theme }) => theme.color.default.main};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const CompactBadge = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 1px 6px;
  border-radius: 999px;
  font-size: 10px;
  font-weight: 700;
  line-height: 1.4;
  flex-shrink: 0;
  color: ${({ $state }) =>
    ATTENDANCE_COLOR[$state] || ATTENDANCE_COLOR.unknown};
  background: ${({ $state }) =>
    ATTENDANCE_BACKGROUND[$state] || ATTENDANCE_BACKGROUND.unknown};
`;

const CompactDot = styled.span`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #d32f2f;
  flex-shrink: 0;
`;

const CompactMuted = styled.span`
  display: block;
  padding: 4px 2px;
  font-size: 12px;
  color: ${({ theme }) => theme.color.default.main};
  font-weight: 500;
`;

function formatRange(item) {
  if (!item) return "";
  return `${item.dateLabel} · ${item.startLabel}–${item.endLabel}`;
}

function attendanceLabel(attendance, t) {
  if (attendance === "present") return t("student.status.present");
  if (attendance === "absent") return t("student.status.absent");
  if (attendance === "unknown") return t("student.status.noLaunch");
  return null;
}

function previousTone(item) {
  if (!item) return "neutral";
  if (item.attendance === "absent") return "absent";
  if (item.attendance === "present") return "present";
  return "neutral";
}

function subjectLine(item) {
  if (!item) return "";
  return item.disciplina || "—";
}

const classItemShape = PropTypes.shape({
  type: PropTypes.string,
  disciplina: PropTypes.string,
  turma: PropTypes.string,
  sala: PropTypes.string,
  professor: PropTypes.string,
  startLabel: PropTypes.string,
  endLabel: PropTypes.string,
  dateLabel: PropTypes.string,
  attendance: PropTypes.oneOf(["present", "absent", "unknown", null])
});

function CompactSlot({ label, item, tone, showAttendance, t }) {
  if (!item) return null;
  const attLabel = showAttendance ? attendanceLabel(item.attendance, t) : null;
  return (
    <CompactChip $tone={tone}>
      <CompactChipHead>
        <CompactChipLabel $tone={tone}>
          {tone === "live" && <CompactDot aria-hidden />}
          {label}
        </CompactChipLabel>
        {attLabel && (
          <CompactBadge $state={item.attendance || "unknown"}>
            {attLabel}
          </CompactBadge>
        )}
      </CompactChipHead>
      <CompactChipTitle title={subjectLine(item)}>
        {subjectLine(item)}
      </CompactChipTitle>
      <CompactChipMeta>{formatRange(item)}</CompactChipMeta>
    </CompactChip>
  );
}

CompactSlot.propTypes = {
  label: PropTypes.string.isRequired,
  item: classItemShape,
  tone: PropTypes.string,
  showAttendance: PropTypes.bool,
  t: PropTypes.func.isRequired
};

CompactSlot.defaultProps = {
  item: null,
  tone: "neutral",
  showAttendance: false
};

/**
 * Timeline do aluno: Aula anterior (com presença) → Atual → Próxima.
 * `compact` = mini-cards para o menu Alunos.
 */
const StudentClassStatus = ({ classStatus, studentId, compact }) => {
  const { t } = useTranslation();
  if (!classStatus) return null;

  const { previous, current, next } = classStatus;
  const hasAny = previous || current || next;

  if (compact) {
    if (!hasAny) {
      return <CompactMuted>{t("student.status.noClassPlanned")}</CompactMuted>;
    }
    return (
      <CompactWrap aria-label={t("student.status.aria")}>
        <CompactSlot
          label={t("student.status.prev")}
          item={previous}
          tone={previousTone(previous)}
          showAttendance
          t={t}
        />
        <CompactSlot
          label={t("student.status.now")}
          item={current}
          tone="live"
          t={t}
        />
        <CompactSlot
          label={t("student.status.next")}
          item={next}
          tone="neutral"
          t={t}
        />
      </CompactWrap>
    );
  }

  if (!hasAny) {
    return (
      <Band aria-label={t("student.status.aria")}>
        <EmptyHint>{t("student.status.emptyWindow")}</EmptyHint>
      </Band>
    );
  }

  const showLink = Boolean(studentId);

  return (
    <Band aria-label={t("student.status.aria")}>
      <Timeline>
        {previous && (
          <Slot $tone={previousTone(previous)}>
            <SlotLabel $tone={previousTone(previous)}>
              {t("student.status.prevClass")}
            </SlotLabel>
            <SlotTitle>
              {previous.disciplina}
              {previous.turma ? ` · ${previous.turma}` : ""}
            </SlotTitle>
            <SlotMeta>
              {formatRange(previous)}
              {previous.sala ? ` · ${previous.sala}` : ""}
            </SlotMeta>
            {attendanceLabel(previous.attendance, t) && (
              <AttendanceBadge $state={previous.attendance || "unknown"}>
                {attendanceLabel(previous.attendance, t)}
              </AttendanceBadge>
            )}
          </Slot>
        )}

        {current && (
          <Slot $tone="live">
            <SlotLabel $tone="live">
              <Dot aria-hidden />
              {t("student.status.inClass")}
            </SlotLabel>
            <SlotTitle>
              {current.disciplina}
              {current.turma ? ` · ${current.turma}` : ""}
            </SlotTitle>
            <SlotMeta>
              {formatRange(current)}
              {current.sala ? ` · ${current.sala}` : ""}
              {current.professor ? ` · ${current.professor}` : ""}
            </SlotMeta>
          </Slot>
        )}

        {next && (
          <Slot $tone="neutral">
            <SlotLabel $tone="neutral">
              {t("student.status.nextClass")}
            </SlotLabel>
            <SlotTitle>
              {next.disciplina}
              {next.turma ? ` · ${next.turma}` : ""}
            </SlotTitle>
            <SlotMeta>
              {formatRange(next)}
              {next.sala ? ` · ${next.sala}` : ""}
            </SlotMeta>
          </Slot>
        )}
      </Timeline>
      {showLink && (
        <ScheduleLink to={`/student/${studentId}/schedule`}>
          {t("student.status.viewSchedule")}
        </ScheduleLink>
      )}
    </Band>
  );
};

StudentClassStatus.propTypes = {
  classStatus: PropTypes.shape({
    previous: classItemShape,
    current: classItemShape,
    next: classItemShape
  }),
  studentId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  compact: PropTypes.bool
};

StudentClassStatus.defaultProps = {
  classStatus: null,
  studentId: null,
  compact: false
};

export default StudentClassStatus;
